import * as React from 'react'
import {observer} from 'mobx-react-lite'
import {Grid, Typography, IconButton, Tooltip} from '@mui/material'
import {makeOcStyles} from '@oc/styles'
import {VisibilityOffIcon, VisibilityOnIcon} from '@oc/icons'
import {lgBreakpoint, mdBreakpoint, smBreakpoint} from './OcGenericDialogInterfaces'
import {OcGenericDialogState} from './OcGenericEditState'
import {OcGenericDialogGroupProps} from './OcGenericDialogInterfacesExport'
import {OcGenericDialogFieldWrapper} from './OcGenericDialogEditComponentWrapper'
import {messages} from '../translations'

interface OcGenericDialogGroupComponentProps<SourceType> extends OcGenericDialogGroupProps<SourceType> {
    groupName: string
    getValueCallback: (group: string, field: string) => any
    dialogState: OcGenericDialogState<SourceType>
}

const useStyles = makeOcStyles(theme => {
    const borderProps = `1px solid ${theme.palette.divider}`

    return {
        groupNameGrid: {
            paddingTop: '12px',
            width: 'calc(100% / 7)',
            [theme.breakpoints.down(lgBreakpoint)]: {
                width: 'calc(100% / 5)'
            },
            [theme.breakpoints.down(mdBreakpoint)]: {
                width: 'calc(100% / 3)'
            },
            [theme.breakpoints.down(smBreakpoint)]: {
                width: 'calc(100% / 2)'
            },
            flexDirection: 'row'
        },
        groupContentGrid: {
            padding: '0 12px',
            width: 'calc((100% / 7) * 6)',
            [theme.breakpoints.down(lgBreakpoint)]: {
                width: 'calc((100% / 5) * 4)'
            },
            [theme.breakpoints.down(mdBreakpoint)]: {
                width: 'calc((100% / 3) * 2)'
            },
            [theme.breakpoints.down(smBreakpoint)]: {
                width: 'calc(100% / 2)'
            }
        },
        groupNameTypo: {
            padding: '12px 0 12px 24px',
            overflowWrap: 'anywhere',
            flexGrow: 1,
            maxWidth: 'calc(100% - 48px)'
        },
        minHeight: {
            height: 'min-content'
        },
        borderTop: {
            borderTop: borderProps
        },
        borderBottom: {
            borderBottom: borderProps
        },
        borderRight: {
            borderRight: borderProps
        }
    }
})

function _OcGenericDialogGroupComponent<SourceType>(props: OcGenericDialogGroupComponentProps<SourceType>) {
    const {groupName, dialogState, getValueCallback, ...groupProps} = props
    const [visible, setVisible] = React.useState(true)
    const {classes, cx} = useStyles()

    const visibleOnChange = React.useCallback(() => {
        setVisible(!visible)
    }, [visible, setVisible])

    const rowStyles = cx({
        [classes.borderTop]: groupProps.borderTop,
        [classes.borderBottom]: groupProps.borderBottom
    })

    return (
        <Grid container item xs={12} data-cy={`oc-generic-dialog-group-${groupName}`} className={rowStyles}>
            <Grid container item className={cx(classes.groupNameGrid, classes.borderRight)}>
                <Typography variant="h4" className={cx(classes.groupNameTypo, classes.minHeight)}>
                    {groupProps.label || groupName}
                </Typography>
                {groupProps.hideable ? (
                    <Tooltip
                        title={
                            visible
                                ? messages.getText('genericDialog', 'hideGroupTooltip')
                                : messages.getText('genericDialog', 'displayGroupTooltip')
                        }
                    >
                        <span className={classes.minHeight}>
                            <IconButton className={classes.minHeight} onClick={visibleOnChange} size="large">
                                {visible ? <VisibilityOffIcon /> : <VisibilityOnIcon />}
                            </IconButton>
                        </span>
                    </Tooltip>
                ) : null}
            </Grid>
            <Grid container item className={classes.groupContentGrid}>
                {visible
                    ? Object.entries(groupProps.fields)
                          .filter(
                              ([_fieldName, fieldProps]) =>
                                  fieldProps &&
                                  (fieldProps.displayCondition
                                      ? fieldProps.displayCondition(dialogState.dialogOutput)
                                      : true)
                          )
                          .sort((field1, field2) => field1[1].index - field2[1].index)
                          .map(([fieldName, fieldProps]) => {
                              return (
                                  <OcGenericDialogFieldWrapper
                                      {...fieldProps}
                                      key={`oc-generic-dialog-group-${groupName}-field-${fieldName}`}
                                      dialogState={dialogState}
                                      group={groupName}
                                      name={fieldName}
                                      getValue={getValueCallback}
                                  />
                              )
                          })
                    : null}
            </Grid>
        </Grid>
    )
}

export const OcGenericDialogGroupComponent = observer(_OcGenericDialogGroupComponent)
